import { Component, input, inject, computed, signal, OnInit, ViewChild, effect, ElementRef } from '@angular/core';

import { GenericTreeEntity } from '../generic-tree.entity';
import { Store } from '@ngxs/store';
import { GenericTreeService, GENERIC_TREE_STATE_NAME } from '../services/generic-tree.entity.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { FormDialogGenericTreeComponent } from '../form-dialog-generic-tree/form-dialog-generic-tree.component';
import { MetadataAssignmentDialogComponent } from '../metadata-assignment-dialog/metadata-assignment-dialog.component';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ConfirmDialogComponent, DeleteDialogData } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { Update } from '../../core/state/entity.state';
import { GenericTreeDragDropService } from '../services/generic-tree-drag-drop.service';
import { GenericTreeExpansionService } from '../services/generic-tree-expansion.service';
import { GenericTreeHighlightService } from '../services/generic-tree-highlight.service';
import { concat, Observable } from 'rxjs';
import { AppSelectors } from '../../app.state';
import { EditorRole } from '../../editor/editor.role.enum';

@Component({
  selector: 'pixl-generic-tree-node',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DragDropModule,
    TranslocoModule
  ],
  templateUrl: './generic-tree-node.component.html',
  styleUrl: './generic-tree-node.component.scss'
})
export class GenericTreeNodeComponent implements OnInit {
  node = input.required<GenericTreeEntity>();
  level = input<number>(1);

  private genericTreeService = inject(GenericTreeService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);
  public dragDropService = inject(GenericTreeDragDropService);
  private expansionService = inject(GenericTreeExpansionService);
  private highlightService = inject(GenericTreeHighlightService);
  private elementRef = inject(ElementRef);

  public userRole = toSignal(this.store.select(AppSelectors.userRole));
  public isAdmin = computed(() => this.userRole() === EditorRole.AppAdmin || this.userRole() === EditorRole.ZAVAdmin);

  manualExpanded = signal(false);

  isExpanded = computed(() => {
    const isDimmed = this.highlightService.isDimmed();
    if (isDimmed) {
      const focusedLevel = this.highlightService.focusedLevel();
      if (focusedLevel !== null) {
        const currentLevel = this.level();
        if (currentLevel < focusedLevel) {
          return this.hasDescendantAtLevel(focusedLevel);
        }
      }
      return false;
    }
    return this.manualExpanded();
  });

  isDimmed = computed(() => {
    if (!this.highlightService.isDimmed()) {
      return false;
    }
    const focusedLevel = this.highlightService.focusedLevel();
    if (focusedLevel === null) {
      return true; // Dim everything if no specific level focused
    }
    return this.level() !== focusedLevel;
  });

  public nodeListId!: string;
  @ViewChild('metadataTooltip') metadataTooltip?: MatTooltip;

  hasMetadata = computed(() => {
    const data = this.node().consumerData;
    if (!data) {
      return false;
    }

    const keys = Object.keys(data);
    if (keys.length === 0) {
      return false;
    }

    for (const key of keys) {
      const value = data[key];

      if (key === 'functionalHelp') {
        const fh = value as { id?: string } | null;
        if (fh && fh.id) {
          return true;
        }
      }
      else {
        // Consider it metadata if the key exists and value is not undefined.
        // Null is valid (means initialized but empty).
        if (value !== undefined) {
          return true;
        }
      }
    }

    return false;
  });

  hasDescendantAtLevel(targetLevel: number): boolean {
    const nodeMap = this.highlightService.nodeMap();
    const levels = this.highlightService.nodeLevels();
    const myId = this.node().id;

    for (const [id, level] of levels.entries()) {
      if (level === targetLevel) {
        let curr = nodeMap.get(id);
        // Walk up from the candidate node until we find 'myId' or reach root
        while (curr && curr.parentId) {
          if (curr.parentId === myId) {
            return true;
          }
          curr = nodeMap.get(curr.parentId);
        }
      }
    }
    return false;
  }

  // Watch the entire state slice
  private state = toSignal(this.store.select(state => state[GENERIC_TREE_STATE_NAME]));

  childNodes = computed(() => {
    const currentState = this.state();

    if (!currentState || !currentState.entities) {
      return [];
    }

    const entities = Object.values(currentState.entities as Record<string, GenericTreeEntity>);
    const currentId = this.node().id;

    const children = entities
      .filter(entity => {
        // Robust check for parentId (handling potential type mismatches)
        return entity.parentId && String(entity.parentId) === String(currentId);
      })
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    return children;
  });

  constructor() {
    effect(() => {
      const isDimmed = this.highlightService.isDimmed();
      const focusedLevel = this.highlightService.focusedLevel();
      const currentLevel = this.level();
      const n = this.node();

      if (isDimmed && focusedLevel !== null) {
        // Auto-scroll logic: Scroll to the first node of the focused level
        if (currentLevel === focusedLevel) {
          const firstNodeId = this.highlightService.firstNodeIdByLevel().get(currentLevel);
          if (firstNodeId === n.id) {
            setTimeout(() => {
              this.elementRef.nativeElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
            }, 0);
          }
        }
      }
    });
  }

  ngOnInit(): void {
    this.nodeListId = `node-list-${this.node().id}`;

    const n = this.node();
    if (n.consumerId && n.subjectId) {
      const expanded = this.expansionService.isExpanded(n.id, n.consumerId, n.subjectId);
      this.manualExpanded.set(expanded);
    }
  }

  toggleExpand(): void {
    this.manualExpanded.update(v => !v);
    const n = this.node();
    if (n.consumerId && n.subjectId) {
      this.expansionService.setExpanded(n.id, n.consumerId, n.subjectId, this.manualExpanded());
    }
  }

  editNode(): void {
    const dialogRef = this.dialog.open(FormDialogGenericTreeComponent, {
      width: '500px',
      data: {
        title: this.translocoService.translate('genericTree.dialog.editNode'),
        genericTree: { ...this.node() }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.genericTreeService.update(result).subscribe();
      }
    });
  }

  deleteNode(): void {
    const dialogData: DeleteDialogData = {
      id: this.node().id,
      name: this.node().name,
      itemType: this.translocoService.translate('genericTree.treeNode'),
      deleteFn: (id: string) => this.genericTreeService.delete(id)
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  addChild(): void {
    const dialogRef = this.dialog.open(FormDialogGenericTreeComponent, {
      width: '500px',
      data: {
        title: this.translocoService.translate('genericTree.dialog.addChildNode'),
        genericTree: {
          consumerId: this.node().consumerId,
          subjectId: this.node().subjectId,
          parentId: this.node().id,
          root: false,
          active: true
        }
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const state = this.store.selectSnapshot(s => s[GENERIC_TREE_STATE_NAME]);
        if (state && state.entities) {
          const entities = Object.values(state.entities as Record<string, GenericTreeEntity>)
            .filter(node => node.consumerId === this.node().consumerId && node.subjectId === this.node().subjectId);

          if (entities.length > 0) {
            const levelMap = this.genericTreeService.calculateNodeLevels(entities);
            const parentLevel = levelMap.get(this.node().id) || 0;
            const targetLevel = parentLevel + 1;

            if (parentLevel > 0) {
              const keys = this.genericTreeService.getMetadataKeysForLevel(entities, targetLevel, levelMap);

              if (keys.length > 0) {
                result.consumerData = result.consumerData || {};
                keys.forEach(key => {
                  if (result.consumerData[key] === undefined) {
                    result.consumerData[key] = null;
                  }
                });
              }
            }
          }
        }

        await this.genericTreeService.create(result);
        await this.genericTreeService.refresh(this.node().id);

        this.manualExpanded.set(true);
        const n = this.node();
        if (n.consumerId && n.subjectId) {
          this.expansionService.setExpanded(n.id, n.consumerId, n.subjectId, true);
        }
      }
    });
  }

  assignMetadata(): void {
    if (!this.hasMetadata()) {
      if (this.metadataTooltip) {
        setTimeout(() => this.metadataTooltip?.show());
      }
      return;
    }

    const dialogRef = this.dialog.open(MetadataAssignmentDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      height: '80vh',
      data: {
        node: { ...this.node(), consumerData: this.node().consumerData || {} }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.genericTreeService.update(result).subscribe();
      }
    });
  }

  drop(event: CdkDragDrop<GenericTreeEntity[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.processUpdates(event.container.data);
    }
    else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      const movedNode = event.container.data[event.currentIndex];

      // Optimistic update for moved node
      const updatedMovedNode = {
        ...movedNode,
        parentId: this.node().id,
        root: false,
        sortOrder: event.currentIndex
      } as GenericTreeEntity;

      this.store.dispatch(new Update(updatedMovedNode));
      const updateObs = this.genericTreeService.update(updatedMovedNode);

      const siblings = event.container.data.filter(n => n.id !== movedNode.id);
      const sortUpdates = this.processUpdates(siblings, false);

      concat(updateObs, ...sortUpdates).subscribe();
    }
  }

  private processUpdates(nodes: GenericTreeEntity[], execute = true): Observable<GenericTreeEntity>[] {
    const updates: Observable<GenericTreeEntity>[] = [];
    nodes.forEach((node, index) => {
      if (node.sortOrder !== index) {
        const updatedNode = { ...node, sortOrder: index };
        this.store.dispatch(new Update(updatedNode));
        updates.push(this.genericTreeService.update(updatedNode));
      }
    });

    if (execute && updates.length > 0) {
      concat(...updates).subscribe();
    }

    return updates;
  }

  trackById(index: number, item: GenericTreeEntity): string {
    return item.id;
  }
}
